import React, { useState, useEffect } from "react";
import {
    Button,
    FormGroup,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Nav,
    NavItem,
    NavLink,
    Card,
    CardBody,
    CardTitle,
    CardText,
    Input
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import classnames from "classnames";
import { createTiktokAd } from "../../../../../slices/veloxity/integration/export/createTiktokAd";
import { getExportPlans } from "../../../../../slices/veloxity/integration/export/getExportPlans";
import {
    showLoadingAlert,
    showSuccessAlert,
    showErrorAlert
} from "../../../../../components/custom/sweetalertHelper";
import Loader from "../../../../../components/common/Loader";

const CreateTiktokAdModal = ({ isOpen, toggle, modalTitle }) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState("1");
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        adAccountIds: "",
    });

    const veloxityAudience = useSelector((state) => state.findAllAudienceReducer?.payload || []);
    const targetAudience = useSelector((state) => state.targetAllAudience?.payload || []);
    const adAccounts = useSelector((state) => state.fetchConfigs?.data || []);
    const loading = useSelector(
        (state) =>
            state.findAllAudienceReducer?.loading ||
            state.targetAllAudience?.loading ||
            state.fetchConfigs?.loading
    );

    const [selectedAudience, setSelectedAudience] = useState(null);
    const [selectedAdAccount, setSelectedAdAccount] = useState(null);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            setSelectedAudience(null);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async () => {
        if (selectedAudience && selectedAdAccount) {
            showLoadingAlert('Submitting...', 'Please wait while your request is being processed.');
    
            const { name, description, adAccountIds } = formData;
    
            const submitData = {
                audienceId: selectedAudience.value,
                integrationConfigId: selectedAdAccount.value,
                name,
                description,
                advertiserId: adAccountIds,
            };
    
            try {
                await dispatch(createTiktokAd(submitData)).unwrap();
                
                showSuccessAlert("TikTok Ad Created", "Your TikTok ad has been successfully created.");
                dispatch(getExportPlans());
                setFormData({ name: "", description: "", adAccountIds: "" });
                setSelectedAudience(null);
                setSelectedAdAccount(null);
                toggle(); 
            } catch (error) {
                showErrorAlert("Creation Failed", "Failed to create TikTok ad. Please try again.");
                console.error("Error creating TikTok Ad:", error); 
            }
        }
    };
    
    const prepareOptions = (items, filterSucceeded = false) => {
        return items
            .filter(item => !filterSucceeded || item.status === "SUCCEEDED") 
            .map(item => ({
                value: item.audienceId || item.id,
                label: item.name,
            }));
    };
    
    const audienceOptions = prepareOptions(activeTab === "1" ? veloxityAudience : targetAudience, true); 
    const adAccountOptions = prepareOptions(adAccounts); 
    
    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>{modalTitle}</ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="text-center">
                        <Loader />
                    </div>
                ) : (
                    <>
                        <Nav tabs>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === "1" })}
                                    onClick={() => toggleTab("1")}
                                >
                                    Audience
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === "2" })}
                                    onClick={() => toggleTab("2")}
                                >
                                    Target Audience
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <div className="mt-3">
                            <FormGroup>
                                <Label style={{ fontWeight: "bold" }}>Select {activeTab === "1" ? "Audience" : "Target Audience"}</Label>
                                <Select
                                    options={audienceOptions}
                                    value={selectedAudience}
                                    onChange={(option) => setSelectedAudience(option)}
                                    isSearchable
                                    placeholder="Search and select an audience..."
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            width: '103%',
                                            marginLeft: '-1.5%',
                                            borderRadius: '0.25rem',
                                            borderColor: '#ced4da',
                                            minHeight: 'calc(1.5em + 0.75rem + 2px)'
                                        }),
                                    }}
                                />
                            </FormGroup>
                        </div>

                        {selectedAudience && (
                            <Card className="mt-3 mx-auto" style={{ width: '98%' }}>
                                <CardBody>
                                    <CardTitle tag="h5" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                        Audience Details
                                    </CardTitle>
                                    <CardText>
                                        <strong>Name:</strong> {selectedAudience.label}
                                    </CardText>
                                </CardBody>
                            </Card>
                        )}

                        <FormGroup className="mt-3">
                            <Label style={{ fontWeight: "bold" }}>Select Ad Account</Label>
                            <Select
                                options={adAccountOptions}
                                value={selectedAdAccount}
                                onChange={(option) => setSelectedAdAccount(option)}
                                isSearchable
                                placeholder="Search and select an ad account..."
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        width: '103%',
                                        marginLeft: '-1.5%',
                                        borderRadius: '0.25rem',
                                        borderColor: '#ced4da',
                                        minHeight: 'calc(1.5em + 0.75rem + 2px)'
                                    }),
                                }}
                            />
                        </FormGroup>

                        <FormGroup className="mt-3">
                            <Label for="name" style={{ fontWeight: 'bold' }}>Name</Label>
                            <Input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter the ad name"
                            />
                        </FormGroup>

                        <FormGroup className="mt-3">
                            <Label for="description" style={{ fontWeight: 'bold' }}>Description</Label>
                            <Input
                                type="text"
                                name="description"
                                id="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Enter a description"
                            />
                        </FormGroup>

                        <FormGroup className="mt-3">
                            <Label for="adAccountIds" style={{ fontWeight: 'bold' }}>Ad Account IDs</Label>
                            <Input
                                type="text"
                                name="adAccountIds"
                                id="adAccountIds"
                                value={formData.adAccountIds}
                                onChange={handleChange}
                                placeholder="Enter Ad Account IDs separated by commas"
                            />
                        </FormGroup>
                        
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>Cancel</Button>
                <Button color="primary" onClick={handleSubmit} disabled={!selectedAudience || !selectedAdAccount || !formData.name || !formData.description}>Submit</Button>
            </ModalFooter>
        </Modal>
    );
};

export default CreateTiktokAdModal;
